/**
 * Google Maps API Service
 *
 * Använder Google Maps JavaScript API:s inbyggda tjänster (inga CORS-problem).
 * Med inbyggd rate limiting för att garantera $0 kostnad.
 *
 * Rate limits:
 * - Max 50 ruttberäkningar/dag
 * - Max 200 geocoding-anrop/dag
 */

import type { Result, LatLng, GeocodingResult } from '@/types';

// Rate limiting konfiguration
const DAILY_ROUTE_LIMIT = 50;
const DAILY_GEOCODE_LIMIT = 200;

// Singleton-instanser för Google Maps-tjänster
let directionsService: google.maps.DirectionsService | null = null;
let geocoder: google.maps.Geocoder | null = null;

/**
 * Google Maps route response
 */
export interface GoogleRoute {
  distance: number; // meters
  duration: number; // seconds
  coordinates: LatLng[];
  summary: string; // Ruttbeskrivning (t.ex. "E4")
}

/**
 * Flera alternativa rutter
 */
export interface GoogleRoutesResult {
  routes: GoogleRoute[];
  selectedIndex: number;
}

/**
 * Hämta API-nyckel från environment
 */
export function getGoogleMapsApiKey(): string {
  const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!key) {
    throw new Error('VITE_GOOGLE_MAPS_API_KEY is not defined in environment');
  }
  return key;
}

/**
 * Hämta eller skapa DirectionsService
 */
function getDirectionsService(): google.maps.DirectionsService {
  if (!directionsService) {
    directionsService = new google.maps.DirectionsService();
  }
  return directionsService;
}

/**
 * Hämta eller skapa Geocoder
 */
function getGeocoder(): google.maps.Geocoder {
  if (!geocoder) {
    geocoder = new google.maps.Geocoder();
  }
  return geocoder;
}

/**
 * Kontrollera och uppdatera rate limit
 */
function checkRateLimit(type: 'route' | 'geocode'): { allowed: boolean; remaining: number } {
  const today = new Date().toDateString();
  const key = `google_${type}_${today}`;
  const count = parseInt(localStorage.getItem(key) || '0');
  const limit = type === 'route' ? DAILY_ROUTE_LIMIT : DAILY_GEOCODE_LIMIT;

  if (count >= limit) {
    return { allowed: false, remaining: 0 };
  }

  localStorage.setItem(key, String(count + 1));
  return { allowed: true, remaining: limit - count - 1 };
}

/**
 * Hämta aktuell rate limit status
 */
export function getRateLimitStatus(): { routes: { used: number; limit: number }; geocode: { used: number; limit: number } } {
  const today = new Date().toDateString();
  const routeCount = parseInt(localStorage.getItem(`google_route_${today}`) || '0');
  const geocodeCount = parseInt(localStorage.getItem(`google_geocode_${today}`) || '0');

  return {
    routes: { used: routeCount, limit: DAILY_ROUTE_LIMIT },
    geocode: { used: geocodeCount, limit: DAILY_GEOCODE_LIMIT },
  };
}

/**
 * Geocoding - Sök efter platser/adresser via Google Maps Geocoder
 */
export async function geocodeAddress(query: string): Promise<Result<GeocodingResult[]>> {
  const rateCheck = checkRateLimit('geocode');
  if (!rateCheck.allowed) {
    return {
      success: false,
      error: new Error(`Daglig gräns för geocoding nådd (${DAILY_GEOCODE_LIMIT}/dag). Försök igen imorgon.`),
    };
  }

  try {
    const geocoderService = getGeocoder();

    return new Promise((resolve) => {
      geocoderService.geocode(
        { address: query, language: 'sv' },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.ZERO_RESULTS || !results || results.length === 0) {
            resolve({ success: true, data: [] });
            return;
          }

          if (status !== google.maps.GeocoderStatus.OK) {
            resolve({
              success: false,
              error: new Error(`Geocoding error: ${status}`),
            });
            return;
          }

          const geocodingResults: GeocodingResult[] = results.slice(0, 5).map((result) => {
            const countryComponent = result.address_components.find(c => c.types.includes('country'));
            const regionComponent = result.address_components.find(c =>
              c.types.includes('administrative_area_level_1') || c.types.includes('administrative_area_level_2')
            );
            const localityComponent = result.address_components.find(c => c.types.includes('locality'));

            return {
              name: localityComponent?.long_name || result.formatted_address.split(',')[0] || result.formatted_address,
              coordinates: {
                lat: result.geometry.location.lat(),
                lon: result.geometry.location.lng(),
              },
              country: countryComponent?.long_name || '',
              region: regionComponent?.long_name,
            };
          });

          resolve({ success: true, data: geocodingResults });
        }
      );
    });
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error('Unknown error'),
    };
  }
}

/**
 * Beräkna rutter mellan två punkter via Google Maps DirectionsService
 * Returnerar flera alternativa rutter om tillgängliga
 */
export async function getDirections(
  start: LatLng,
  end: LatLng
): Promise<Result<GoogleRoutesResult>> {
  const rateCheck = checkRateLimit('route');
  if (!rateCheck.allowed) {
    return {
      success: false,
      error: new Error(`Daglig gräns för ruttberäkning nådd (${DAILY_ROUTE_LIMIT}/dag). Försök igen imorgon.`),
    };
  }

  try {
    const service = getDirectionsService();

    return new Promise((resolve) => {
      service.route(
        {
          origin: { lat: start.lat, lng: start.lon },
          destination: { lat: end.lat, lng: end.lon },
          travelMode: google.maps.TravelMode.DRIVING,
          provideRouteAlternatives: true, // Hämta alternativa rutter
        },
        (result, status) => {
          if (status !== google.maps.DirectionsStatus.OK || !result) {
            resolve({
              success: false,
              error: new Error(`Directions error: ${status}`),
            });
            return;
          }

          if (result.routes.length === 0) {
            resolve({
              success: false,
              error: new Error('No route found'),
            });
            return;
          }

          // Konvertera alla rutter
          const routes: GoogleRoute[] = result.routes.map((route) => {
            const leg = route.legs[0];
            const coordinates: LatLng[] = route.overview_path.map(point => ({
              lat: point.lat(),
              lon: point.lng(),
            }));

            return {
              distance: leg?.distance?.value || 0,
              duration: leg?.duration?.value || 0,
              coordinates,
              summary: route.summary || 'Rutt',
            };
          });

          resolve({
            success: true,
            data: {
              routes,
              selectedIndex: 0,
            },
          });
        }
      );
    });
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error('Unknown error'),
    };
  }
}

/**
 * Interpolera en punkt längs ett segment
 */
function interpolatePoint(
  from: LatLng,
  to: LatLng,
  fraction: number
): LatLng {
  return {
    lat: from.lat + (to.lat - from.lat) * fraction,
    lon: from.lon + (to.lon - from.lon) * fraction,
  };
}

/**
 * Sampla punkter längs en rutt med exakt jämna intervall
 * Interpolerar för att få exakt avstånd mellan punkterna
 */
export function sampleRoutePoints(
  coordinates: LatLng[],
  intervalKm: number = 50
): LatLng[] {
  if (coordinates.length < 2) return coordinates;

  const firstCoord = coordinates[0];
  if (!firstCoord) return coordinates;

  const points: LatLng[] = [firstCoord];
  let distanceToNextPoint = intervalKm;

  for (let i = 1; i < coordinates.length; i++) {
    const prev = coordinates[i - 1];
    const curr = coordinates[i];

    if (!prev || !curr) continue;

    const segmentDistance = haversineDistance(prev, curr);
    let remainingInSegment = segmentDistance;

    while (remainingInSegment >= distanceToNextPoint) {
      const distanceFromOriginalPrev = segmentDistance - remainingInSegment + distanceToNextPoint;
      const interpolationFraction = distanceFromOriginalPrev / segmentDistance;

      const interpolatedPoint = interpolatePoint(prev, curr, interpolationFraction);
      points.push(interpolatedPoint);

      remainingInSegment -= distanceToNextPoint;
      distanceToNextPoint = intervalKm;
    }

    distanceToNextPoint -= remainingInSegment;
  }

  const lastCoord = coordinates[coordinates.length - 1];
  if (lastCoord) {
    const lastPoint = points[points.length - 1];
    if (!lastPoint || haversineDistance(lastPoint, lastCoord) > 1) {
      points.push(lastCoord);
    }
  }

  return points;
}

/**
 * Beräkna avstånd mellan två punkter (Haversine formula)
 */
export function haversineDistance(a: LatLng, b: LatLng): number {
  const R = 6371; // Jordens radie i km
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);

  const sinLat = Math.sin(dLat / 2);
  const sinLon = Math.sin(dLon / 2);

  const calc = sinLat * sinLat +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLon * sinLon;

  return R * 2 * Math.atan2(Math.sqrt(calc), Math.sqrt(1 - calc));
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

/**
 * Konvertera LatLng till Google Maps-format för kartan
 */
export function toGoogleLatLng(coord: LatLng): google.maps.LatLngLiteral {
  return { lat: coord.lat, lng: coord.lon };
}

/**
 * Konvertera Google Maps LatLng till vår LatLng
 */
export function fromGoogleLatLng(coord: google.maps.LatLngLiteral): LatLng {
  return { lat: coord.lat, lon: coord.lng };
}
